import { useState, useEffect } from 'react';
import { handleApiError, withRetry } from '@/lib/utils/api-error-handler';

interface BlogInteractions {
  viewCount: number;
  likeCount: number;
  liked: boolean;
  comments: any[];
  isLoading: {
    views: boolean;
    likes: boolean;
    comments: boolean;
  };
  error: {
    views: string | null;
    likes: string | null;
    comments: string | null;
  };
  incrementView: () => Promise<void>;
  toggleLike: () => Promise<void>;
  addComment: (comment: { name: string; email?: string; content: string; parentId?: string | null }) => Promise<void>;
  refreshComments: () => Promise<void>;
}

export function useBlogInteractions(slug: string): BlogInteractions {
  const [viewCount, setViewCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState({
    views: true,
    likes: true,
    comments: true,
  });
  const [error, setError] = useState({
    views: null as string | null,
    likes: null as string | null,
    comments: null as string | null,
  });

  // Fetch initial data
  useEffect(() => {
    if (!slug) return;
    
    fetchViewCount();
    fetchLikeStatus();
    fetchComments();
  }, [slug]);

  // Increment view count when page loads
  useEffect(() => {
    if (!slug) return;
    
    incrementView();
  }, [slug]);

  // Fetch view count
  const fetchViewCount = async () => {
    try {
      setIsLoading(prev => ({ ...prev, views: true }));
      
      // Use withRetry for automatic retries with exponential backoff
      const fetchWithRetry = withRetry(async () => {
        const response = await fetch(`/api/blog/views?slug=${encodeURIComponent(slug)}`);
        
        if (!response.ok) {
          throw response;
        }
        
        return await response.json();
      });
      
      const data = await fetchWithRetry();
      setViewCount(data.viewCount || 0);
      setError(prev => ({ ...prev, views: null }));
    } catch (err) {
      console.error('Error fetching view count:', err);
      const errorMessage = await handleApiError(err, 'Failed to fetch view count');
      setError(prev => ({ ...prev, views: errorMessage }));
      // Provide a fallback value so the UI doesn't break
      setViewCount(0);
    } finally {
      setIsLoading(prev => ({ ...prev, views: false }));
    }
  };

  // Increment view count
  const incrementView = async () => {
    try {
      // Use withRetry for automatic retries with exponential backoff
      const incrementWithRetry = withRetry(async () => {
        const response = await fetch('/api/blog/views', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug }),
        });
        
        if (!response.ok) {
          throw response;
        }
        
        return await response.json();
      });
      
      const data = await incrementWithRetry();
      setViewCount(data.viewCount || 0);
      setError(prev => ({ ...prev, views: null }));
    } catch (err) {
      console.error('Error incrementing view count:', err);
      const errorMessage = await handleApiError(err, 'Failed to increment view count');
      setError(prev => ({ ...prev, views: errorMessage }));
      // Silent failure - don't break the user experience for view counts
    }
  };

  // Fetch like status and count
  const fetchLikeStatus = async () => {
    try {
      setIsLoading(prev => ({ ...prev, likes: true }));
      
      // Use withRetry for automatic retries with exponential backoff
      const fetchWithRetry = withRetry(async () => {
        const response = await fetch(`/api/blog/likes?slug=${encodeURIComponent(slug)}`);
        
        if (!response.ok) {
          throw response;
        }
        
        return await response.json();
      });
      
      const data = await fetchWithRetry();
      setLiked(data.liked || false);
      setLikeCount(data.likeCount || 0);
      setError(prev => ({ ...prev, likes: null }));
    } catch (err) {
      console.error('Error fetching like status:', err);
      const errorMessage = await handleApiError(err, 'Failed to fetch like status');
      setError(prev => ({ ...prev, likes: errorMessage }));
      // Provide fallback values so the UI doesn't break
      setLiked(false);
      setLikeCount(0);
    } finally {
      setIsLoading(prev => ({ ...prev, likes: false }));
    }
  };

  // Toggle like status
  const toggleLike = async () => {
    try {
      setIsLoading(prev => ({ ...prev, likes: true }));
      
      // Use withRetry for automatic retries with exponential backoff
      const toggleWithRetry = withRetry(async () => {
        const response = await fetch('/api/blog/likes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug }),
        });
        
        if (!response.ok) {
          throw response;
        }
        
        return await response.json();
      });
      
      const data = await toggleWithRetry();
      setLiked(data.liked);
      setLikeCount(data.likeCount);
      setError(prev => ({ ...prev, likes: null }));
    } catch (err) {
      console.error('Error toggling like:', err);
      const errorMessage = await handleApiError(err, 'Failed to toggle like');
      setError(prev => ({ ...prev, likes: errorMessage }));
      // Keep previous state to avoid UI disruption
    } finally {
      setIsLoading(prev => ({ ...prev, likes: false }));
    }
  };

  // Fetch comments
  const fetchComments = async () => {
    try {
      setIsLoading(prev => ({ ...prev, comments: true }));
      
      // Use withRetry for automatic retries with exponential backoff
      const fetchWithRetry = withRetry(async () => {
        const response = await fetch(`/api/blog/comments?slug=${encodeURIComponent(slug)}`);
        
        if (!response.ok) {
          throw response;
        }
        
        return await response.json();
      });
      
      const data = await fetchWithRetry();
      setComments(data.comments || []);
      setError(prev => ({ ...prev, comments: null }));
    } catch (err) {
      console.error('Error fetching comments:', err);
      const errorMessage = await handleApiError(err, 'Failed to fetch comments');
      setError(prev => ({ ...prev, comments: errorMessage }));
      // Provide fallback empty array so UI doesn't break
      setComments([]);
    } finally {
      setIsLoading(prev => ({ ...prev, comments: false }));
    }
  };

  // Add a comment
  const addComment = async (comment: { name: string; email?: string; content: string; parentId?: string | null }) => {
    try {
      setIsLoading(prev => ({ ...prev, comments: true }));
      
      // Use withRetry for automatic retries with exponential backoff
      const addWithRetry = withRetry(async () => {
        const response = await fetch('/api/blog/comments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slug,
            name: comment.name,
            email: comment.email,
            content: comment.content,
            parentId: comment.parentId,
          }),
        });
        
        if (!response.ok) {
          throw response;
        }
        
        return await response.json();
      });
      
      await addWithRetry();
      
      // Refresh comments after adding a new one
      await fetchComments();
      setError(prev => ({ ...prev, comments: null }));
    } catch (err) {
      console.error('Error adding comment:', err);
      const errorMessage = await handleApiError(err, 'Failed to add comment');
      setError(prev => ({ ...prev, comments: errorMessage }));
    } finally {
      setIsLoading(prev => ({ ...prev, comments: false }));
    }
  };

  // Limit repeated request attempts with a cooldown mechanism
  const [commentRefreshCooldown, setCommentRefreshCooldown] = useState(false);

  const refreshComments = async () => {
    // Don't attempt another refresh if we're already in cooldown
    if (commentRefreshCooldown) {
      return;
    }
    
    try {
      setCommentRefreshCooldown(true);
      await fetchComments();
    } catch (err) {
      console.error('Error refreshing comments:', err);
      // Error handling is already done in fetchComments
    } finally {
      // Set a cooldown period of 5 seconds between refresh attempts
      setTimeout(() => setCommentRefreshCooldown(false), 5000);
    }
  };
  
  return {
    viewCount,
    likeCount,
    liked,
    comments,
    isLoading,
    error,
    incrementView,
    toggleLike,
    addComment,
    refreshComments
  };
}
